import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import type { WorkOrderStatusEnum } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { gqlDeleteWorkOrder } from 'pages/Project/UerProject/WorkOrder/workOrderQueries';
import { ExportCSVReport } from 'utils/createCSVReportFile';

import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { useProjectParams } from './useProjectParams';
import { useSendGpnAnalyticsEvent } from './useSendGpnAnalyticsEvent';

export const useMethodsWorkOrder = (workOrderId: number, statusWorkOrder?: WorkOrderStatusEnum) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { id: projectId, methodology } = useProjectParams();
    const { projectData, refreshProject } = useProjectData(projectId, false);

    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

    const [deleteData, deleteStatus, deleteError, deleteWorkOrder] = useRequest({
        requestId: 'deleteWorkOrder',
        query: gqlDeleteWorkOrder,
        withEffect: false,
    });

    useEffect(() => {
        if (deleteStatus === RequestStatus.SUCCESS && deleteData?.uerDeleteWorkOrder?.result) {
            if (workOrderId && deleteData?.uerDeleteWorkOrder?.id === workOrderId) {
                const newPathName = `/projects/${methodology}/${projectId}/contract`;

                if (pathname === newPathName) {
                    refreshProject();
                } else {
                    navigate(newPathName);
                }
            }
        }
    }, [
        workOrderId,
        deleteStatus,
        deleteData?.uerDeleteWorkOrder?.id,
        deleteData?.uerDeleteWorkOrder?.result,
        projectId,
        pathname,
        navigate,
        refreshProject,
        methodology,
    ]);

    const handleDeleteButton = useCallback(() => {
        sendGpnAnalyticsEvent({
            componentId: 'rim_order delete button_pressed',
            projectId,
            contractId: projectData?.contractId,
            orderId: workOrderId,
            orderStatus: statusWorkOrder,
            methodology: projectData?.methodology,
            projectName: projectData?.name,
            projectType: projectData?.contractType,
            projectDO: projectData?.subsidiary?.name,
        });

        setIsDialogOpen(true);
    }, [
        projectData?.contractId,
        projectData?.contractType,
        projectData?.methodology,
        projectData?.name,
        projectData?.subsidiary?.name,
        projectId,
        sendGpnAnalyticsEvent,
        statusWorkOrder,
        workOrderId,
    ]);

    const handleDialogClose = useCallback(() => {
        setIsDialogOpen(false);
    }, []);

    const handleDeleteWorkOrder = useCallback(() => {
        setIsSubmitted(true);
        dispatch(deleteWorkOrder({ workOrderId: Number(workOrderId) }));
    }, [deleteWorkOrder, dispatch, workOrderId]);

    const handleCloseModalError = useCallback(() => {
        setIsSubmitted(false);
    }, []);

    const handleCreateReport = () => {
        ExportCSVReport(deleteData?.uerDeleteWorkOrder?.validation ?? []);
    };

    return {
        deleteData,
        deleteStatus,
        deleteError,
        deleteWorkOrder,
        isSubmitted,
        isDialogOpen,
        setIsDialogOpen,
        handleDeleteButton,
        handleDialogClose,
        handleDeleteWorkOrder,
        handleCloseModalError,
        handleCreateReport,
    };
};
